import TodoItem from "@/domain/entities/TodoItem";
import { useToast } from "@/hooks/utils/useToast";
import { useTodos } from "@cs_teamwaydev/mailvista-sdk_react";
import { useTranslation } from "react-i18next";

const useManageTodo = (): {
    setTodoState: (todo: TodoItem, todoState: boolean) => Promise<boolean>;
    deleteTodo: (todo: TodoItem) => Promise<boolean>;
    addTodoToDashboard: (todo: TodoItem) => Promise<boolean>;
} => {
    const todos = useTodos();
    const { toast } = useToast();
    const { t } = useTranslation();

    const handleAction = async (
        action: () => Promise<boolean>,
        errorMsg: string,
    ): Promise<boolean> => {
        try {
            const success = await action();
            if (!success) {
                throw new Error(errorMsg);
            }

            return true;
        } catch (error) {
            console.error(errorMsg, error);
            toast({
                title: t("errors.todoActions"),
                description: t("errors.global.description"),
                variant: "error",
            });

            return false;
        }
    };

    const setTodoState = async (todo: TodoItem, todoState: boolean) =>
        handleAction(
            () =>
                todos.setTodoState(todo.internetMessageId, todo.id, todoState),
            "Failed to set todo state",
        );

    const deleteTodo = async (todo: TodoItem) =>
        handleAction(
            () =>
                todos.removeTodoFromDashboard(todo.internetMessageId, todo.id),
            "Failed to delete todo",
        );

    const addTodoToDashboard = async (todo: TodoItem) =>
        handleAction(
            () => todos.addTodoToDashboard(todo.internetMessageId, todo.id),
            "Failed to add todo to dashboard",
        );

    return { setTodoState, deleteTodo, addTodoToDashboard };
};

export default useManageTodo;
